import { useStaff } from '@hook'
import { TriDot } from '@icons'
import { ADMIN_MODAL_TYPE, useModalStore } from '@modal'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { showStaffDetail } from '@store/slicers/ModalSlicer'
import { PatientIncluded } from '@type/patient.types'
import { StaffIncluded, StaffSAttrProps } from '@type/staff.types'
import { DropdownBtn, Loader } from '@ui'
import { capitalizeEachLetter, getError, showToast } from '@util'
import { Box, DropButton, Text } from 'grommet'
import { CaretRightFill, Checkmark } from 'grommet-icons'
import { useMemo, useState } from 'react'

type Props = StaffSAttrProps & {
  included: StaffIncluded[]
}

export const getEmail = (included: StaffIncluded[] | PatientIncluded[], id: string) => {
  return included.find(({ id: value, type }) => type === 'user' && id === value)?.attributes.email || ''
}
const DropContent = ({ included, ...datum }: Props) => {
  const { staff_status_transitions } = useAppSelector(state => state.config)
  const { openModal } = useModalStore()
  const dispatch = useAppDispatch()
  const { updateStatus, resendEmail, resendEmailIsPending } = useStaff()
  const [open, setOpen] = useState(false)

  const onOpen = () => {
    setOpen(true)
  }
  const onCloseAll = () => {
    setOpen(false)
  }

  const handleChangeStatus = async ({
    staffId,
    status,
    onClose
  }: {
    staffId: string
    status: string
    onClose?: () => void
  }) => {
    onCloseAll()
    onClose && onClose()
    try {
      await updateStatus({ formVal: { status }, id: staffId })
      showToast(`updated status to ${status.replace(/_/g, ' ')}`)
    } catch (error) {
      getError(error)
    }
  }

  const handleResendEmail = async () => {
    try {
      await resendEmail(datum?.id)
      showToast('Email sent successfully')
      onCloseAll()
    } catch (error) {
      getError(error)
    }
  }

  const options = useMemo(() => {
    let array: [] | string[] = []
    if (datum?.meta?.status === 'dismissed') return []
    if (staff_status_transitions) array = [...staff_status_transitions?.[datum?.meta?.status]]
    return array
  }, [staff_status_transitions, datum?.meta?.status])

  return (
    <>
      <DropButton
        alignSelf="center"
        dropContent={
          <Box pad="small" responsive style={{ display: 'block' }}>
            <Box
              onClick={() => {
                dispatch(showStaffDetail({ id: datum?.id }))
                onCloseAll()
              }}
              pad={{ bottom: 'small' }}
            >
              <Text size="14px">View Details</Text>
            </Box>
            {datum?.meta?.status !== 'pending' && (
              <DropdownBtn
                dropProps={{ align: { right: 'left', top: 'top' }, style: { padding: '10px 0' } }}
                dropdownIcon={<CaretRightFill />}
                dropdownLabel="Change Status"
                dropdownTitle="Status"
                margin={{ bottom: 'small' }}
                options={[
                  {
                    activeBtn: true,
                    handleClick: onCloseAll,
                    icon: <Checkmark size="small" />,
                    label: capitalizeEachLetter(datum?.meta?.status.replace(/_/g, ' ')),
                    reverse: true
                  },
                  ...options?.map(item => {
                    return {
                      activeBtn: datum?.meta?.status === item,
                      handleClick: (onClose: () => void) =>
                        handleChangeStatus({ onClose, staffId: datum?.id, status: item }),
                      icon: datum?.meta?.status === item ? <Checkmark size="small" /> : <></>,
                      label: capitalizeEachLetter(item.replace(/_/g, ' ')),
                      reverse: true
                    }
                  })
                ]}
                plain
                reverse
              />
            )}
            {datum?.meta?.status === 'pending' && (
              <Box onClick={handleResendEmail} pad={{ bottom: 'small' }}>
                <Text size="14px">Resend Email</Text>
              </Box>
            )}
            <Box
              onClick={() => {
                openModal(ADMIN_MODAL_TYPE.ADD_STAFF, { id: datum?.id })
                onCloseAll()
              }}
              pad={{ bottom: 'small' }}
            >
              <Text size="14px">Edit Staff</Text>
            </Box>
            <Box
              className="!hidden"
              onClick={() => {
                onCloseAll()
              }}
              pad={{ bottom: 'small' }}
            >
              <Text size="14px">Edit Access</Text>
            </Box>
            {/* <Box
            onClick={() => {
              dispatch(showDeleteStaff({ id: datum?.id }))
              onCloseAll()
            }}
          >
            <Text size="14px">Remove Staff</Text>
          </Box> */}
          </Box>
        }
        dropProps={{ align: { right: 'left', top: 'top' } }}
        margin={{ vertical: 'small' }}
        onClose={onCloseAll}
        onOpen={onOpen}
        open={open}
      >
        <TriDot />
      </DropButton>
      {resendEmailIsPending && <Loader />}
    </>
  )
}

export default DropContent
